import { useContext, useEffect, useState } from 'react';
import { useCustomerYorentClient } from '../../../../clients/customer-api/customer-yorent-api-client';
import { CheckoutSubmitRequest } from '../../checkout-view';
import { useCustomer } from '../../../../hooks/use-customer-context';
import { TenantContext } from '../../../../context/tenant-context';
import { useCustomerAPI } from '../../../../clients/customer-api/customer-api-client';
import { ecommerceToast } from '../../../../util/toasts';
import { FinancialSummary, LineItem } from '../../../../clients/quipli-service/financials/financials';
import { convertToMoney } from '../../../../util/util';
import { CustomerOrder } from '../../../../clients/customer-api/order/customer-order';
import { Cart } from '../../../../clients/customer-api/cart/cart';
import { Location } from '../../../../clients/quipli-service/location/location';
import {
  generateAdditionalDiscountLineItem,
  generateCart,
  generateCouponLineItem,
  generateDamageWaiverLineItem,
  generateDeliveryFeeLineItem,
  generateFeeLineItems,
  generatePickupFeeLineItem,
  generateProductLineItems,
  generateSecurityDepositLineItem,
  generateTaxLineItem,
} from '../utils/cart-summary';

const useQuote = (orderId: number) => {
  const { getOrderById, getLocationById, calculateQuoteFinancialSummary, acceptQuote } = useCustomerAPI();
  const { refreshCartProductCount } = useCustomerYorentClient();
  const { contextPath } = useContext(TenantContext);
  const customer = useCustomer();

  const [order, setOrder] = useState<CustomerOrder>();
  const [location, setLocation] = useState<Location>();
  const [cart, setCart] = useState<Cart>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [summary, setSummary] = useState<FinancialSummary>();

  const handleDeliveryAddressChange = (req: { deliveryAddressId?: number }) => {
    if (!location) return;

    calculateQuoteFinancialSummary({
      quoteId: orderId,
      locationId: location.id,
      deliveryAddressId: req.deliveryAddressId,
    }).then((res) => setSummary(res));
  };

  const generateSummary = (_order: CustomerOrder, _location: Location) => {
    const currency = _location.currency as Dinero.Currency;

    const { productFeeLineItems, productLineItems } = generateProductLineItems(_order, currency);

    const feeLineItems = generateFeeLineItems(_order, currency);
    const deliveryFee = generateDeliveryFeeLineItem(_order, currency);
    const pickupFee = generatePickupFeeLineItem(_order, currency);

    const lineItems: LineItem[] = [
      ...productLineItems,
      ...productFeeLineItems,
      ...feeLineItems,
      deliveryFee,
      pickupFee,
    ];

    const damageWaiver = generateDamageWaiverLineItem(_order, currency);
    if (damageWaiver) {
      lineItems.push(damageWaiver);
    }

    const additionalDiscount = generateAdditionalDiscountLineItem(_order, currency);
    if (additionalDiscount) {
      lineItems.push(additionalDiscount);
    }

    const tax = generateTaxLineItem(_order, currency);
    if (tax) {
      lineItems.push(tax);
    }

    const coupon = generateCouponLineItem(_order, currency);
    if (coupon) {
      lineItems.push(coupon);
    }

    const securityDeposit = generateSecurityDepositLineItem(_order, currency);
    if (securityDeposit) {
      lineItems.push(securityDeposit);
    }

    setSummary({
      total: convertToMoney(_order.netCharged, currency),
      lineItems,
    });
  };

  useEffect(() => {
    const init = async () => {
      const order = await getOrderById(orderId);
      const location = await getLocationById(order.locationId);

      setOrder(order);
      setLocation(location);

      setCart(generateCart(order));

      generateSummary(order, location);
    };

    init();
  }, [orderId]);

  const onSubmit = async (req: CheckoutSubmitRequest) => {
    if (isSubmitting || !location || !summary || !cart || !customer) return;

    setIsSubmitting(true);
    try {
      await acceptQuote(
        orderId,
        location.id,
        req.paymentMethod,
        req.billingAddressId,
        req.deliveryAddressId,
        req.digitalSignatureDate,
      );
      await refreshCartProductCount();
      window.location.href = `${contextPath}custom/payment_success`;
    } catch (error: any) {
      ecommerceToast(error.message.msg || error.message || 'Error accepting quote.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    order,
    location,
    cart,
    isSubmitting,
    summary,
    onSubmit,
    handleDeliveryAddressChange,
  };
};

export default useQuote;
